import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/app-config.service';
import { LanguageService } from '@app/core/services/translate/language.service';
import { Page } from '@app/shared/models/page.interface';
import { SearchParams } from '@app/shared/models/search-params.model';
import { DataTablePageState } from '@components/data/data-table-new/directives/datatable-pagination.directive';
import { DataTableSortState } from '@components/data/data-table-new/directives/datatable-sort.directive';
import { Observable, of } from 'rxjs';
import { HierarchyType, hierarchyTypes } from '../models/hierarchy.constants';
import {
  FormHierarchyDto,
  FormNodeDto,
  GetHierarchyDto,
  HierarchyTypeServiceDto,
  NodeDto,
  NodeElementDto,
  NodeTypeDto,
  TreeNodeState
} from '../models/hierarchy.model';

@Injectable({ providedIn: 'root' })
export class HierarchyService {
  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    protected languageService: LanguageService
  ) {}

  getAll(endpoint: string, page: DataTablePageState, sort: DataTableSortState, filter: string): Observable<Page<any>> {
    let params = new HttpParams()
      .append('filter', filter)
      .append('page', page.index.toString())
      .append('size', page.size.toString())
      .append('sort', sort.column + ',' + sort.direction);

    return this.http.get<Page<any>>(this.appConfigService.getApi() + endpoint, {
      params
    });
  }

  /**
   * Retrieves the Node children of the root Node in the hierarchy.
   * @param hierarchyId hierarchyId
   */
  getHierarchyRootNode(hierarchyId: string): Observable<NodeDto> {
    return this.http.get<NodeDto>(this.appConfigService.getApi() + '/hierarchy/hierarchies/' + hierarchyId + '/root');
  }

  /**
   * Retrieves the Node children of a given Node in the hierarchy.
   * @param hierarchyId hierarchyId
   * @param nodeId nodeId
   */
  getHierarchyNodeChildren(hierarchyId: string, nodeId: string): Observable<NodeDto[]> {
    return this.http.get<NodeDto[]>(
      this.appConfigService.getApi() + '/hierarchy/hierarchies/' + hierarchyId + '/nodes/' + nodeId + '/children'
    );
  }

  /**
   * Retrieves the Node corresponding to the given id.
   * @param id id
   */
  getHierarchyNode(hierarchyId: string, nodeId: string): Observable<NodeDto> {
    return this.http.get<NodeDto>(
      this.appConfigService.getApi() + '/hierarchy/hierarchies/' + hierarchyId + '/nodes/' + nodeId
    );
  }

  /**
   * Retrieves a Page of the Node children of a given Node in the hierarchy.
   * @param hierarchyId hierarchyId
   * @param nodeId nodeId
   * @param state state
   */
  getHierarchyNodeChildrenPaged(hierarchyId: string, nodeId: string, state: TreeNodeState): Observable<Page<NodeDto>> {
    const params: SearchParams = {
      search: state && state.filter ? state.filter : '',
      filter: '',
      pageNumber: state && state.page ? state.page : 0,
      pageSize: state && state.size ? state.size : 10,
      sortDirection: state && state.direction ? state.direction.toString().toUpperCase() : 'ASC',
      sortField: state && state.column ? state.column : 'nodeElementDescription'
    };

    return this.http.post<Page<NodeDto>>(
      this.appConfigService.getApi() + '/hierarchy/hierarchies/' + hierarchyId + '/nodes/' + nodeId + '/children/paged',
      params
    );
  }

  /**
   * Retrieves the list of temporal nodes which matches the filters and the upper nodes until reaching the root.
   * @param hierarchyId
   * @param filter
   */
  getHierarchyNodesRecursive(hierarchyId: string, filter: string): Observable<NodeDto[]> {
    let params = new HttpParams().append('filter', filter);

    return this.http.get<NodeDto[]>(
      this.appConfigService.getApi() + '/hierarchy/hierarchies/' + hierarchyId + '/nodes/recursive',
      { params }
    );
  }

  /**
   * Retrieves the list of temporal nodes which matches the filters and the upper nodes until reaching the root.
   * @param hierarchyId
   * @param filter
   */
  getHierarchyEditNodesRecursive(hierarchyId: string, filter: string): Observable<NodeDto[]> {
    let params = new HttpParams().append('filter', filter);

    return this.http.get<NodeDto[]>(
      this.appConfigService.getApi() + '/hierarchy/hierarchies/' + hierarchyId + '/edit/nodes/recursive',
      { params }
    );
  }

  /**
   * Retrieves all Hierarchy Types services.
   */
  getHierarchiesServices(): Observable<HierarchyTypeServiceDto[]> {
    return this.http.get<HierarchyTypeServiceDto[]>(`${this.appConfigService.getApi()}/hierarchy/hierarchies/services`);
  }

  /**
   * Retrieves a Hierarchy Types services give nits Id.
   * @param hierarchyTypeServiceId hierarchyTypeServiceId
   */
  getHierarchiesService(hierarchyTypeServiceId: string): Observable<HierarchyTypeServiceDto> {
    return this.http.get<HierarchyTypeServiceDto>(
      `${this.appConfigService.getApi()}/hierarchy/hierarchies/services/${hierarchyTypeServiceId}`
    );
  }

  /**
   * Retrieves a Page of NodeElement depending on the serviceId.
   * @param nodeTypeId nodeTypeId
   * @param page page
   * @param sort sort
   * @param filter filter
   */
  getNodeElementPage(
    nodeTypeId: string,
    page: DataTablePageState,
    sort: DataTableSortState,
    filter: string
  ): Observable<Page<any>> {
    let params = new HttpParams()
      .append('filter', filter)
      .append('page', page.index.toString())
      .append('size', page.size.toString())
      .append('sort', sort.column + ',' + sort.direction);
    return this.http.get<Page<any>>(
      `${this.appConfigService.getApi()}/hierarchy/nodeTypes/${nodeTypeId}/nodeElements/paged`,
      {
        params
      }
    );
  }

  /**
   * Retrieves a Page of NodeElement depending on the serviceId.
   */
  getAllNodeTypes(): Observable<NodeTypeDto[]> {
    return this.http.get<NodeTypeDto[]>(this.appConfigService.getApi() + '/hierarchy/nodetypes/all');
  }

  /**
   * Retrieves the Node Type corresponding to the given id.
   * @param id id
   */
  getNodeType(id: string): Observable<NodeTypeDto> {
    return this.http.get<NodeTypeDto>(`${this.appConfigService.getApi()}/hierarchy/nodetypes/${id}`);
  }

  getNewNodeType(): Observable<NodeTypeDto> {
    return of<NodeTypeDto>({} as NodeTypeDto);
  }

  saveNodeTypeSetting(nodeType: NodeTypeDto): Observable<NodeTypeDto> {
    return nodeType.id != null ? this.updateNodeTypeSetting(nodeType) : this.createNodeTypeSetting(nodeType);
  }

  /**
   * Creates a Node Type
   * @param nodeType nodeType
   */
  createNodeTypeSetting(nodeType: NodeTypeDto): Observable<NodeTypeDto> {
    return this.http.post<NodeTypeDto>(`${this.appConfigService.getApi()}/hierarchy/nodetypes`, nodeType);
  }

  /**
   * Updates a Node Type
   * @param nodeType nodeType
   */
  updateNodeTypeSetting(nodeType: NodeTypeDto): Observable<NodeTypeDto> {
    return this.http.put<NodeTypeDto>(`${this.appConfigService.getApi()}/hierarchy/nodetypes/${nodeType.id}`, nodeType);
  }

  /**
   * Deletes a Node Type
   * @param id id
   */
  deleteNodeTypeSetting(id: string): Observable<any> {
    return this.http.delete<any>(`${this.appConfigService.getApi()}/hierarchy/nodetypes/${id}`);
  }

  /**
   * Retrieves all the Hierarchies in the system.
   */
  getAllHierarchies(): Observable<GetHierarchyDto[]> {
    return this.http.get<GetHierarchyDto[]>(this.appConfigService.getApi() + '/hierarchy/hierarchies/all');
  }

  getAllHierarchiesWithoutDivisions(): Observable<GetHierarchyDto[]> {
    return this.http.get<GetHierarchyDto[]>(this.appConfigService.getApi() + '/hierarchy/hierarchies/all/division');
  }

  /**
   * Retrieves the Hierarchy corresponding to the given id.
   * @param hierarchyId hierarchyId
   */
  getHierarchy(hierarchyId: string): Observable<GetHierarchyDto> {
    return this.http.get<GetHierarchyDto>(this.appConfigService.getApi() + '/hierarchy/hierarchies/' + hierarchyId);
  }

  getNewHierarchy(): Observable<GetHierarchyDto> {
    return of<GetHierarchyDto>({ divisions: [] } as GetHierarchyDto);
  }

  saveHierarchySetting(hierarchy: FormHierarchyDto): Observable<GetHierarchyDto> {
    return hierarchy.id != null
      ? this.updateHierarchySetting(hierarchy.id, hierarchy)
      : this.createHierarchySetting(hierarchy);
  }

  /**
   * Creates a Hierarchy
   * @param hierarchy hierarchy
   */
  createHierarchySetting(hierarchy: FormHierarchyDto): Observable<GetHierarchyDto> {
    return this.http.post<GetHierarchyDto>(`${this.appConfigService.getApi()}/hierarchy/hierarchies`, hierarchy);
  }

  /**
   * Updates a Hierarchy
   * @param id id
   * @param hierarchy hierarchy
   */
  updateHierarchySetting(id: string, hierarchy: FormHierarchyDto): Observable<GetHierarchyDto> {
    return this.http.put<GetHierarchyDto>(`${this.appConfigService.getApi()}/hierarchy/hierarchies/${id}`, hierarchy);
  }

  /**
   * Deletes a Hierarchy
   * @param id id
   */
  deleteHierarchySetting(id: string): Observable<any> {
    return this.http.delete<any>(`${this.appConfigService.getApi()}/hierarchy/hierarchies/${id}`);
  }

  /**
   * Saves the temporal hierarchy that may have been modified before.
   * @param hierarchyId hierarchyId
   */
  saveTree(hierarchyId: string): Observable<any> {
    return this.http.post<any>(
      `${this.appConfigService.getApi()}/hierarchy/hierarchies/${hierarchyId}/edit/save`,
      null
    );
  }

  /**
   * Removes all temporal changes made in the hierarchy that may have been modified before.
   * @param hierarchyId hierarchyId
   */
  cancelTree(hierarchyId: string): Observable<any> {
    return this.http.post<any>(
      `${this.appConfigService.getApi()}/hierarchy/hierarchies/${hierarchyId}/edit/cancel`,
      null
    );
  }

  /**
   * Deletes a list of Nodes.
   * @param hierarchyId hierarchyId
   * @param nodeIds nodeIds
   */
  deleteNode(hierarchyId: string, nodeIds: string[]): Observable<void> {
    return this.http.post<void>(
      `${this.appConfigService.getApi()}/hierarchy/hierarchies/${hierarchyId}/edit/deleteNodes`,
      nodeIds
    );
  }

  /**
   * Updates a Node.
   * @param hierarchyId hierarchyId
   * @param node node
   */
  updateNode(hierarchyId: string, node: FormNodeDto): Observable<NodeDto> {
    return this.http.put<NodeDto>(
      `${this.appConfigService.getApi()}/hierarchy/hierarchies/${hierarchyId}/edit/nodes/${node.id}`,
      node
    );
  }

  /**
   * Move a Node to another parent.
   * @param hierarchyId hierarchyId
   * @param children children
   * @param parentId parentId
   */
  moveNodeToAnotherParent(hierarchyId: string, children: string[], parentId: string): Observable<any> {
    return this.http.post<any>(
      this.appConfigService.getApi() + `/hierarchy/hierarchies/${hierarchyId}/edit/nodes/move`,
      {
        children,
        parentId
      }
    );
  }

  /** Temporal methods */

  /**
   * Retrieves the TEMPORAL Node children of the root Node in the hierarchy.
   * @param hierarchyId hierarchyId
   */
  getTemporalHierarchyRootNode(hierarchyId: string): Observable<NodeDto> {
    return this.http.get<NodeDto>(
      this.appConfigService.getApi() + '/hierarchy/hierarchies/' + hierarchyId + '/edit/root'
    );
  }

  /**
   * Retrieves the Node children of a given Node in the hierarchy.
   * @param hierarchyId hierarchyId
   * @param nodeId nodeId
   */
  getTemporalHierarchyNodeChildren(hierarchyId: string, nodeId: string): Observable<NodeDto[]> {
    return this.http.get<NodeDto[]>(
      `${this.appConfigService.getApi()}/hierarchy/hierarchies/${hierarchyId}/edit/nodes/${nodeId}/children`
    );
  }

  /**
   * Retrieves a Page of the TEMPORAL Node children of a given Node in the hierarchy.
   * @param hierarchyId hierarchyId
   * @param nodeId nodeId
   * @param state state
   */
  getTemporalHierarchyNodeChildrenPaged(
    hierarchyId: string,
    nodeId: string,
    state: TreeNodeState
  ): Observable<Page<NodeDto>> {
    const params: SearchParams = {
      search: state && state.filter ? state.filter : '',
      filter: '',
      pageNumber: state && state.page ? state.page : 0,
      pageSize: state && state.size ? state.size : 10,
      sortDirection: state && state.direction ? state.direction.toString().toUpperCase() : 'ASC',
      sortField: state && state.column ? state.column : 'nodeElementDescription'
    };

    return this.http.post<Page<NodeDto>>(
      this.appConfigService.getApi() +
        '/hierarchy/hierarchies/' +
        hierarchyId +
        '/edit/nodes/' +
        nodeId +
        '/children/paged',
      params
    );
  }

  /**
   * Retrieves a list of TEMPORAL parent nodes of a node in a hierarchy sorting by level asc.
   * @param node node
   */
  getTemporalBreadcrumbs(node: NodeDto): Observable<NodeDto[]> {
    return this.http.get<NodeDto[]>(
      `${this.appConfigService.getApi()}/hierarchy/hierarchies/${node.hierarchyId}/edit/nodes/${node.id}/breadcrumbs`
    );
  }

  /**
   * Retrieves a TEMPORAL Node in the hierarchy.
   * @param hierarchyId hierarchyId
   * @param nodeId nodeId
   */
  getTemporalNode(hierarchyId: string, nodeId: string): Observable<NodeDto> {
    return this.http.get<NodeDto>(
      `${this.appConfigService.getApi()}/hierarchy/hierarchies/${hierarchyId}/edit/nodes/${nodeId}`
    );
  }

  /**
   * Creates a list of Nodes.
   * @param hierarchyId hierarchyId
   * @param nodes nodes
   */
  createTemporalNodes(hierarchyId: string, nodes: NodeDto[]): Observable<NodeDto[]> {
    return this.http.post<NodeDto[]>(
      `${this.appConfigService.getApi()}/hierarchy/hierarchies/${hierarchyId}/edit/nodes`,
      nodes
    );
  }

  /**
   * Refresh the temporal tree with the master data.
   * @param hierarchyId hierarchyId
   */
  refreshTemporalTree(hierarchyId: string): Observable<void> {
    return this.http.post<void>(
      `${this.appConfigService.getApi()}/hierarchy/hierarchies/${hierarchyId}/edit/refresh`,
      null
    );
  }

  getNodeDescriptor(node: NodeDto): string {
    return node.nodeElementDescriptionTranslation
      ? this.languageService.getCurrentLangTxOrNoTx(node.nodeElementDescriptionTranslation)
      : node.nodeElementDescription;
  }

  getNodeElementDescriptor(node: NodeElementDto): string {
    return node.descriptionTx ? this.languageService.getCurrentLangTxOrNoTx(node.descriptionTx) : node.description;
  }

  getNodeNoTxClass(node: NodeDto): string {
    return this.getNodeDescriptor(node) === this.languageService.getNoTx() ? 'no-tx' : '';
  }

  getNodeElementNoTxClass(node: NodeElementDto) {
    return this.getNodeElementDescriptor(node) === this.languageService.getNoTx() ? 'no-tx' : '';
  }

  isHierarchyTypeEditable(hierarchyType2: HierarchyType): boolean {
    return hierarchyType2 !== hierarchyTypes.CUS && hierarchyType2 !== hierarchyTypes.PRD;
  }

  getNodeTypeDescription(hierarchy: GetHierarchyDto, node: NodeDto): string {
    const nodeType = hierarchy.nodeTypes.find(nt => nt.id === node.nodeTypeId);
    return nodeType && nodeType.nodeTag !== 'ROOT' ? `(${nodeType.description})` : '';
  }
}
